import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Chart as ChartJS, TooltipModel, registerables } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { DashboardDataRaw, DateRange, Granularity, PurchaserPlanRaw } from '../../types/Dashboard';
import {
  bucketize,
  altasRowsInBucket,
  bajasRowsInBucket,
  planNamesFromRaw,
  suscriptorLabel,
} from './dashboardHelpers';
import { Panel } from '../ui';
import {
  planColor,
  withAlpha,
  formatNumber,
  legendStyle,
  tooltipStyle,
  tickStyle,
  axisTitleStyle,
  yGrid,
} from './dashboardUi';

ChartJS.register(...registerables);

interface Props {
  data: DashboardDataRaw;
  range: DateRange;
  granularity: Granularity;
}

const GRAN_LABEL: Record<Granularity, string> = {
  day: 'día',
  week: 'semana',
  month: 'mes',
};

const BAJA_SUFFIX = ' (baja)';

/** Tope de suscriptores listados por plan en el tooltip; el resto va como "y N más". */
const MAX_DETALLE = 12;

const TOOLTIP_WIDTH = 320;

/** Secciones del tooltip; colores claros para que se lean sobre el fondo oscuro. */
const SECCIONES = [
  { tipo: 'altas' as const, titulo: 'Altas', color: '#34d399' }, // emerald-400
  { tipo: 'bajas' as const, titulo: 'Bajas', color: '#f87171' }, // red-400
];
const TOOLTIP_GAP = 14;

interface GrupoTooltip {
  key: string;
  plan: string;
  tipo: 'altas' | 'bajas';
  color: string;
  cantidad: number;
  nombres: string[];
}

interface TooltipState {
  /** Punto de anclaje en coordenadas de viewport (position: fixed). */
  x: number;
  y: number;
  title: string;
  grupos: GrupoTooltip[];
}

const nombresOrdenados = (rows: PurchaserPlanRaw[] | undefined): string[] =>
  (rows ?? []).map(suscriptorLabel).sort((a, b) => a.localeCompare(b, 'es'));

const AltasBajasChart: React.FC<Props> = ({ data, range, granularity }) => {
  const { labels, datasets, detalle, totalAltas, totalBajas } = useMemo(() => {
    const buckets = bucketize(range, granularity);
    const planNames = planNamesFromRaw(data.premium);
    const labels = buckets.map((b) => b.label);

    // Filas de cada bucket, calculadas una sola vez: de acá salen las barras
    // y el detalle de suscriptores del tooltip.
    const altasPorBucket = buckets.map((b) => altasRowsInBucket(data.premium, b));
    const bajasPorBucket = buckets.map((b) => bajasRowsInBucket(data.premium, b));

    // Series positivas: altas por plan
    const altaDatasets = planNames.map((plan) => ({
      label: plan,
      data: altasPorBucket.map((byPlan) => byPlan[plan]?.length ?? 0),
      backgroundColor: planColor(plan),
      stack: 'altas',
      borderRadius: 3,
      maxBarThickness: 34,
    }));

    // Series negativas: bajas por plan, mismo color pero translúcido para que
    // se lean como el reverso de la serie de arriba y no como otro plan.
    const bajasDatasets = planNames.map((plan) => ({
      label: `${plan}${BAJA_SUFFIX}`,
      data: bajasPorBucket.map((byPlan) => -(byPlan[plan]?.length ?? 0)),
      backgroundColor: withAlpha(planColor(plan), 0.4),
      stack: 'bajas',
      borderRadius: 3,
      maxBarThickness: 34,
    }));

    const sum = (rows: { data: number[] }[]) =>
      rows.reduce((acc, ds) => acc + ds.data.reduce((s, n) => s + n, 0), 0);

    // detalle[datasetIndex][dataIndex] = "Apellido, Nombre · DNI" ordenados,
    // en el mismo orden que los datasets. Solo por día o semana: por mes son
    // demasiados nombres y el tooltip deja de leerse.
    const conDetalle = granularity !== 'month';
    const nombres = (rows: PurchaserPlanRaw[] | undefined) => (conDetalle ? nombresOrdenados(rows) : []);
    const detalle: string[][][] = [
      ...planNames.map((plan) => altasPorBucket.map((byPlan) => nombres(byPlan[plan]))),
      ...planNames.map((plan) => bajasPorBucket.map((byPlan) => nombres(byPlan[plan]))),
    ];

    return {
      labels,
      datasets: [...altaDatasets, ...bajasDatasets],
      detalle,
      totalAltas: sum(altaDatasets),
      totalBajas: Math.abs(sum(bajasDatasets)),
    };
  }, [data, range, granularity]);

  const chartData = { labels, datasets };

  // Tooltip en HTML (por portal): el de canvas no puede pasar del alto del
  // gráfico y con la lista de suscriptores se cortaba.
  const [tip, setTip] = useState<TooltipState | null>(null);
  const tipKey = useRef<string | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [tipTop, setTipTop] = useState<number | null>(null);

  const externalTooltip = useCallback(
    ({ chart, tooltip }: { chart: ChartJS; tooltip: TooltipModel<'bar'> }) => {
      if (tooltip.opacity === 0 || tooltip.dataPoints.length === 0) {
        if (tipKey.current !== null) {
          tipKey.current = null;
          setTip(null);
        }
        return;
      }
      const rect = chart.canvas.getBoundingClientRect();
      const x = rect.left + tooltip.caretX;
      const y = rect.top + tooltip.caretY;
      const dataIndex = tooltip.dataPoints[0].dataIndex;
      // Solo re-renderizar cuando cambia la barra o se movió el gráfico.
      const key = `${dataIndex}|${Math.round(rect.left)}|${Math.round(rect.top)}`;
      if (key === tipKey.current) return;
      tipKey.current = key;

      const grupos: GrupoTooltip[] = tooltip.dataPoints
        .filter((dp) => (dp.parsed.y ?? 0) !== 0)
        .map((dp) => {
          const label = dp.dataset.label ?? '';
          const esBaja = label.endsWith(BAJA_SUFFIX);
          return {
            key: `${dp.datasetIndex}`,
            plan: label.replace(BAJA_SUFFIX, ''),
            tipo: esBaja ? 'bajas' : 'altas',
            // Color pleno del plan también en las bajas: el translúcido de la
            // barra se pierde sobre el fondo oscuro del tooltip.
            color: planColor(label.replace(BAJA_SUFFIX, '')),
            cantidad: Math.abs(dp.parsed.y ?? 0),
            nombres: detalle[dp.datasetIndex]?.[dp.dataIndex] ?? [],
          };
        });
      setTip({ x, y, title: tooltip.title.join(' '), grupos });
    },
    [detalle]
  );

  // Ubica el tooltip verticalmente dentro de la ventana una vez medido.
  useLayoutEffect(() => {
    if (!tip || !tipRef.current) {
      setTipTop(null);
      return;
    }
    const h = tipRef.current.offsetHeight;
    const max = window.innerHeight - h - 8;
    setTipTop(Math.max(8, Math.min(tip.y - h / 2, max)));
  }, [tip]);

  const tipLeft = tip
    ? tip.x + TOOLTIP_GAP + TOOLTIP_WIDTH > window.innerWidth
      ? Math.max(8, tip.x - TOOLTIP_GAP - TOOLTIP_WIDTH)
      : tip.x + TOOLTIP_GAP
    : 0;

  // Memoizado: si cambiara en cada render, mostrar el tooltip (setState) haría
  // que react-chartjs-2 actualice el gráfico en cada movimiento del mouse.
  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        ...legendStyle,
        labels: {
          ...legendStyle.labels,
          // Un plan, una entrada: la serie de bajas repetía la leyenda entera
          // y la duplicaba a lo ancho del gráfico.
          filter: (item: { text: string }) => !item.text.endsWith(BAJA_SUFFIX),
        },
      },
      tooltip: {
        enabled: false,
        external: externalTooltip,
        // Con dos stacks por bucket la mitad de las series vale 0; listarlas
        // llena el tooltip de ruido.
        filter: (item: { parsed: { y: number | null } }) => (item.parsed.y ?? 0) !== 0,
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: { ...tickStyle, maxRotation: 0, autoSkipPadding: 16 },
        grid: { display: false },
        border: { color: '#e2e8f0' },
      },
      y: {
        stacked: true,
        title: { display: true, text: 'Suscriptores', ...axisTitleStyle },
        ticks: { ...tickStyle, callback: (v: string | number) => formatNumber(Math.abs(Number(v))) },
        grid: yGrid,
        border: { display: false },
      },
    },
  }), [externalTooltip]);

  return (
    <Panel
      title={`Altas y bajas por ${GRAN_LABEL[granularity]}`}
      subtitle="Hacia arriba las altas, hacia abajo las bajas, apiladas por plan. Las bajas usan el mismo color del plan en versión clara."
      actions={
        <div className="flex items-center gap-4 text-xs tabular-nums">
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span className="text-slate-400">Altas</span>
            <strong className="font-semibold text-emerald-600">{formatNumber(totalAltas)}</strong>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            <span className="text-slate-400">Bajas</span>
            <strong className="font-semibold text-red-500">{formatNumber(totalBajas)}</strong>
          </span>
        </div>
      }
    >
      <div className="h-72" onMouseLeave={() => { tipKey.current = null; setTip(null); }}>
        <Bar data={chartData} options={options} />
      </div>
      {tip &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            className="pointer-events-none fixed z-50 overflow-hidden rounded-lg px-3 py-2.5 text-xs shadow-lg"
            style={{
              left: tipLeft,
              top: tipTop ?? tip.y,
              width: TOOLTIP_WIDTH,
              maxHeight: 'calc(100vh - 16px)',
              visibility: tipTop === null ? 'hidden' : 'visible',
              backgroundColor: tooltipStyle.backgroundColor,
              color: tooltipStyle.bodyColor,
              fontFamily: tooltipStyle.bodyFont.family,
            }}
          >
            <div className="mb-1.5 font-semibold" style={{ color: tooltipStyle.titleColor }}>
              {tip.title}
            </div>
            <div className="divide-y divide-slate-700">
              {SECCIONES.map(({ tipo, titulo, color }) => {
                const grupos = tip.grupos.filter((g) => g.tipo === tipo);
                if (grupos.length === 0) return null;
                const total = grupos.reduce((s, g) => s + g.cantidad, 0);
                return (
                  <section key={tipo} className="py-2 first:pt-0 last:pb-0">
                    <div
                      className="mb-1.5 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-wide tabular-nums"
                      style={{ color }}
                    >
                      <span>{titulo}</span>
                      <span>{formatNumber(total)}</span>
                    </div>
                    <div className="space-y-1.5">
                      {grupos.map((g) => (
                        <div key={g.key}>
                          <div className="flex items-center gap-1.5 tabular-nums">
                            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: g.color }} />
                            <span className="min-w-0 flex-1 truncate">{g.plan}</span>
                            <strong className="font-semibold">{formatNumber(g.cantidad)}</strong>
                          </div>
                          {g.nombres.length > 0 && (
                            <ul className="mt-0.5 space-y-px pl-3.5 text-slate-400">
                              {g.nombres.slice(0, MAX_DETALLE).map((n, i) => (
                                <li key={i} className="truncate">{n}</li>
                              ))}
                              {g.nombres.length > MAX_DETALLE && (
                                <li className="italic">y {formatNumber(g.nombres.length - MAX_DETALLE)} más</li>
                              )}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </Panel>
  );
};

export default AltasBajasChart;
